/**
 * Unsynced-deck bookkeeping — which decks hold LOCAL card edits that still
 * depend on a later reconnect flush to reach the server.
 *
 * Why it exists: an edit made while the socket is `ready` is pushed by the
 * dirty scan's microtask flush. An edit made while the socket is NOT ready
 * (disconnected, syncing, error) is pushed later by `flushLocal`, which walks
 * the RESIDENT card buckets only (`define-sync-entity.ts`). Once card bodies are
 * hydrated lazily, a deck edited offline in one session may not be resident in
 * the next — and its edit would never be pushed. The tracker remembers those
 * decks so hydrate can force-load them before the socket's first flush, and
 * so the flush can forget them once it has covered them.
 *
 * Semantics:
 *   - `mark(deckId)` is called by every local card mutator; it records nothing
 *     while `syncReady` is true (the dirty flush owns those edits).
 *   - `markUnanswered(deckId)` is called for every card push held for the
 *     server's answer, ready or not, and cleared by that answer.
 *   - each mark bumps the deck's GENERATION. A flush captures `(id → gen)` for
 *     the decks whose full row set was in its snapshot and, after its sends,
 *     clears only the ids whose generation is unchanged — an edit that lands
 *     between the snapshot and the sends keeps its mark.
 *   - the id set is persisted (`settings` row `unsyncedDeckIds`) on change
 *     only; generations are in-memory.
 *
 * This applies with or without lazy hydration: today an offline edit to a deck
 * that later goes cold (bodies evicted) is stranded the same way.
 */
import { getSyncStorage } from '../../provider/sync-storage';
import { safePut } from '../store/store-utils';

/** `settings` row key holding the persisted id list (`{ key, value: string[] }`). */
export const UNSYNCED_DECKS_SETTINGS_KEY = 'unsyncedDeckIds';

export type UnsyncedDeckTracker = {
  /** Record a local card edit to `deckId`. No-op while the socket is ready. */
  mark(deckId: string): void;
  /** Record that a card push for `deckId` was SENT but not yet answered —
   *  also while the socket is ready: a half-open socket may have swallowed
   *  it, and an app killed before the answer must still re-send it. */
  markUnanswered(deckId: string): void;
  /** Mirror of the sync status: `true` exactly while it is `ready`. */
  setSyncReady(ready: boolean): void;
  isSyncReady(): boolean;
  has(deckId: string): boolean;
  /** Marked deck ids (insertion order). */
  ids(): string[];
  /** Snapshot `(id → generation)` of the marked decks passing `filter`. */
  capture(filter?: (deckId: string) => boolean): ReadonlyMap<string, number>;
  /** Forget every captured id whose generation is unchanged since the capture. */
  clearCaptured(captured: ReadonlyMap<string, number>): void;
  /** Replace the set from its persisted form (hydrate). Does not persist. */
  load(ids: readonly string[]): void;
  /** Forget everything (user switch). Does not persist. */
  reset(): void;
};

export function createUnsyncedDeckTracker(
  persist: (ids: string[]) => void,
): UnsyncedDeckTracker {
  const generations = new Map<string, number>();
  let syncReady = false;

  const ids = () => [...generations.keys()];

  const bump = (deckId: string) => {
    const isNew = !generations.has(deckId);
    generations.set(deckId, (generations.get(deckId) ?? 0) + 1);
    if (isNew) persist(ids());
  };

  return {
    mark(deckId) {
      if (syncReady) return;
      bump(deckId);
    },
    markUnanswered: bump,
    setSyncReady(ready) {
      syncReady = ready;
    },
    isSyncReady: () => syncReady,
    has: (deckId) => generations.has(deckId),
    ids,
    capture(filter) {
      const out = new Map<string, number>();
      for (const [id, gen] of generations) {
        if (!filter || filter(id)) out.set(id, gen);
      }
      return out;
    },
    clearCaptured(captured) {
      let changed = false;
      for (const [id, gen] of captured) {
        if (generations.get(id) === gen) {
          generations.delete(id);
          changed = true;
        }
      }
      if (changed) persist(ids());
    },
    load(next) {
      generations.clear();
      for (const id of next) {
        if (typeof id === 'string' && id.length > 0) generations.set(id, 1);
      }
    },
    reset() {
      generations.clear();
    },
  };
}

/** Persist the id list beside the other client-local settings rows. */
function persistUnsyncedDeckIds(ids: string[]): void {
  safePut(getSyncStorage(), 'settings', { key: UNSYNCED_DECKS_SETTINGS_KEY, value: ids });
}

let tracker: UnsyncedDeckTracker | null = null;

/** Process-wide tracker (one per session; the store's `reset()` clears it). */
export function getUnsyncedDeckTracker(): UnsyncedDeckTracker {
  if (!tracker) tracker = createUnsyncedDeckTracker(persistUnsyncedDeckIds);
  return tracker;
}

/** Test-only: drop the singleton so specs start from an empty set. */
export function _resetUnsyncedDeckTracker(): void {
  tracker = null;
}
